import axios, {AxiosError, AxiosResponse} from "axios";
import BaseService from "./base-service";
import {Schema, SchemaResponse} from "../types/Schema";
import * as Endpoints from './endpoints';
import * as SomlUtils from '../utils/soml-utils';
import * as ContentTypes from './content-types';
import {SchemaStatistic} from "../types/SchemaStatistics";
import {Configurations} from "../types/Configurations";
import Notification from "../common/Notification";
import {
  convertToValidationResult,
  transformDataValidationResponse
} from "../utils/validation-utils";
import {DataValidationReport, ValidationResultModel} from "./models/validation-model";

const HYDRA_ITEMS = 'hydra:totalItems';
const HYDRA_MEMBER = SomlUtils.SCHEMA_HYDRA_MEMBER;

/**
 * Service for performing REST request to the /soml API.
 */
class SomlService extends BaseService {

  /**
   * Retrieve all schemas and maps their properties into {@link Schema}.
   *
   * @returns {Promise<Array<Schema>>}
   */
  static getAllSchemas(size?: number): Promise<Array<Schema>> {
    const params: any = {...SomlService.getPagingParams(size)};
    // This returns json+ld
    return axios.get(Endpoints.SOML, {params}).then((response: AxiosResponse) => {
      if (response.data[HYDRA_ITEMS] && response.data[HYDRA_ITEMS] > 0) {
        return response.data[HYDRA_MEMBER].map(SomlUtils.parseSchemaJsonLd);
      }
      return [];
    }).catch(this.resolveErrorResponse);
  }

  private static getPagingParams(size?: number) {
    const params: any = {};
    if (size) {
      params.size = size;
    }
    return params;
  }

  /**
   * Returns the specified SOML schema.
   *
   * @param {string} id - the schema to retrieve
   * @returns {Promise<Schema>}
   */
  static getSchema(id: string): Promise<Schema> {
    return axios.get(`${Endpoints.SOML}/${id}`, {
      headers: {
        'Accept': ContentTypes.APPLICATION_LD_JSON
      }
    }).then(this.resolveSchema)
      .catch(this.resolveErrorResponse);
  }

  /**
   * Run data validation against concrete SOML schema.
   * @param schemaId The schema id.
   */
  static validateData(schemaId: string): Promise<any> {
    return axios.post(`${Endpoints.SOML}/${schemaId}/validate`, null, {
      headers: {
        'Accept': ContentTypes.APPLICATION_LD_JSON
      }
    }).catch(this.resolveErrorResponse);
  }

  static cancelDataValidation(schemaId: string): Promise<any> {
    return axios.post(`${Endpoints.SOML}/${schemaId}/validate`, null, {
      params: {
        cancel: true
      },
      headers: {
        'Accept': ContentTypes.APPLICATION_LD_JSON
      }
    })
      .then(this.resolveDataValidationReport)
      .catch(this.resolveErrorResponse);
  }

  static getDataValidationReport(schemaId: string, all: boolean = true): Promise<DataValidationReport> {
    return axios.get(`${Endpoints.SOML}/${schemaId}/validate`, {
      params: {
        all
      },
      headers: {
        'Accept': ContentTypes.APPLICATION_LD_JSON
      }
    })
      .then(this.resolveDataValidationReport)
      .catch(this.resolveErrorResponse);
  }

  static getValidationMessages(schemaId: string): Promise<ValidationResultModel> {
    return this.getDataValidationReport(schemaId, true)
      .then((report) => convertToValidationResult(report));
  }

  static getDataValidationStatus(schemaId: string): Promise<DataValidationReport> {
    return this.getDataValidationReport(schemaId, false);
  }

  /**
   * Validates the provided schema content.
   *
   * @param {string} schema - the schema to validate
   * @returns {Promise<SchemaResponse>}
   */
  static validateSchema(schema: string): Promise<SchemaResponse> {
    // Call soaas validation service and store the response.
    // If there is an error, then store the error too, but don't throw it so that the promise chain continues.
    // Call search service validation and store the response.
    // If there is an error, then store it.
    // The final handler:
    // If there are no errors, get one of the responses and resolve with it, as they both should be pretty much the same.
    // If there are errors, then get the errors and aggregate them. Then reject with the aggregated error.
    let soaasSomlResponse: SchemaResponse;
    let soaasErrorResponse: AxiosError;
    let searchSomlResponse: SchemaResponse;
    let searchErrorResponse: AxiosError;
    return SomlService.soaasSomlValidate(schema)
      .then((response: SchemaResponse) => {
        soaasSomlResponse = response;
        return soaasSomlResponse;
      }).catch((error: AxiosError) => {
        soaasErrorResponse = error;
        return soaasErrorResponse;
      }).then(() => {
        return SomlService.searchSomlValidate(schema);
      }).then((response: SchemaResponse) => {
        searchSomlResponse = response;
        return searchSomlResponse;
      }).catch((error: AxiosError) => {
        searchErrorResponse = error;
        return searchErrorResponse;
      }).then(() => {
        if (soaasSomlResponse && searchSomlResponse) {
          return soaasSomlResponse;
        } else {
          let errors;
          // merge them
          if (soaasErrorResponse) {
            errors = SomlService.resolveErrorResponseData(soaasErrorResponse);
          }
          if (searchErrorResponse) {
            const searchErrors = SomlService.resolveErrorResponseData(searchErrorResponse);
            if (errors && errors[HYDRA_MEMBER]) {
              errors[HYDRA_MEMBER].push(...searchErrors[HYDRA_MEMBER]);
            } else {
              errors = searchErrors;
            }
          }
          return Promise.reject(errors);
        }
      });
  }

  private static soaasSomlValidate(schema: string) {
    return axios.post(Endpoints.SOML_VALIDATE, schema, {
      headers: {
        'Content-type': ContentTypes.TEXT_YAML,
        'Accept': ContentTypes.APPLICATION_LD_JSON
      }
    }).then(SomlService.resolveSchemaResponse);
  }

  private static searchSomlValidate(schema: string) {
    return axios.post(Endpoints.SEARCH_VALIDATE, schema, {
      headers: {
        'Content-type': ContentTypes.TEXT_YAML
      }
    }).then(SomlService.resolveSchemaResponse);
  }

  /**
   * Saves a soml schema provided as a yaml formatted string.
   *
   * @param {string} schema - the schema to be saved
   * @return {Promise<SchemaResponse>}
   */
  static saveSchema(schema: string): Promise<SchemaResponse> {
    return axios.post(Endpoints.SOML, schema, {
      headers: {
        'Accept': ContentTypes.APPLICATION_LD_JSON,
        'Content-type': ContentTypes.TEXT_YAML
      }
    }).then(this.resolveSchemaResponse);
  }

  /**
   * Updates a SOML schema.
   *
   * @param {string} id - the schema ID to update
   * @param {string} schema - the updated schema
   * @returns {Promise<SchemaResponse>}
   */
  static updateSchema(id: string, schema: string): Promise<SchemaResponse> {
    return axios.put(`${Endpoints.SOML}/${id}`, schema, {
      headers: {
        'Accept': ContentTypes.APPLICATION_LD_JSON,
        'Content-type': ContentTypes.TEXT_YAML
      }
    }).then(this.resolveSchemaResponse)
      .catch(this.resolveErrorResponse);
  }

  private static activationSuccess(id: string, service: string, response: any) {
    Notification.success({message: `Schema ${id} was activated in ${service}`});
    return response;
  }

  private static activationError(id: string, service: string, errorResponse: any) {
    console.error(errorResponse);
    const message = `Schema ${id} activation in ${service} failed.`;
    Notification.error({
      message, dismiss: {duration: 15000, onScreen: true}
    });
    return Promise.reject(new Error(message));
  }

  private static activateSomlInSoaas(id: string) {
    return axios.put(`${Endpoints.SOML}/${id}/soaas`)
      .then((response) => this.activationSuccess(id, 'SOaaS', response))
      .catch((errorResponse) => this.activationError(id, 'SOaaS', errorResponse));
  }

  private static activateSomlInSearch(id: string) {
    return axios.put(`${Endpoints.SEARCH_SOML}/${id}/search`)
      .then((response) => this.activationSuccess(id, 'Search', response))
      .catch((errorResponse) => this.activationError(id, 'Search', errorResponse));
  }

  /**
   * Tries to activate SOML schema in SOaaS and Search service if available.
   * Activation is done concurrently SOaaS first and then in the Search service.
   *
   * If either operation fails, the error is handled and a notification is displayed.
   *
   * @param {string} id
   * @param {Configurations} configurations
   * @returns {Promise<T>}
   */
  static activateSchema(id: string, configurations: Configurations) {
    const promises = [this.activateSomlInSoaas(id)];
    if (configurations.endpoints.graphql.search) {
      // Check if there is search graphql endpoint configured and try to bind the schema in the
      // search service as well.
      promises.push(this.activateSomlInSearch(id));
    }
    return Promise.allSettled(promises).then((results: Array<PromiseSettledResult<any>>) => {
      const soaasResponse: PromiseSettledResult<any> = results[0];
      if (soaasResponse.status === "fulfilled") {
        return this.resolveResponse(soaasResponse.value);
      }
      // The error should be handled already. Just resolve to allow schema status to change in UI.
      return Promise.resolve(soaasResponse.reason);
    });
  }

  /**
   * Fetch a bound SOML schema statistics.
   *
   * @param {string} id - id of the schema to be activated
   * @param {number} limit - how many statistic entries to return
   * @returns {Promise<Array<SchemaStatistic>>}
   */
  static getSchemaStatistics(id: string, limit: number): Promise<Array<SchemaStatistic>> {
    return axios.get(`${Endpoints.SOML}/${id}/stats`, {
      headers: {
        'Accept': ContentTypes.APPLICATION_LD_JSON
      },
      params: {
        limit
      }
    }).then(this.resolveResponse)
      .then(this.toSchemaStatistics)
      .catch(this.resolveErrorResponse);
  }

  /**
   * Downloads the given SOML schema.
   *
   * @param {string} id he ID of the schema to be downloaded
   * @returns {Promise<Blob>}
   */
  static downloadSchema(id: string): Promise<Blob> {
    return axios.get(`${Endpoints.DOWNLOAD_SOML}/${id}`, {
      responseType: 'blob'
    }).then(this.resolveResponse)
      .catch((error) => {
        if (error.response && error.response.data) {
          // Data is a Blob -> convert to text
          return error.response.data.text().then((err: any) => {
            return Promise.reject(err);
          });
        }
        return Promise.reject(error);
      });
  }

  /**
   * Deletes the given SOML schema.
   *
   * @param {string} id the ID of the schema to be deleted
   * @returns {Promise<void>}
   */
  static deleteSchema(id: string): Promise<void> {
    return axios.delete(`${Endpoints.SOML}/${id}`)
      .then(this.resolveResponse)
      .catch(this.resolveErrorResponse);
  }

  private static toSchemaStatistics(data: any): Array<SchemaStatistic> {
    const stats = data.shapesEstimates || {};
    return Object.keys(stats).map(key => {
      return {
        stat: key,
        value: stats[key]
      }
    });
  }

  private static resolveDataValidationReport(response: AxiosResponse): DataValidationReport {
    const responseData = SomlService.resolveResponse(response);
    return transformDataValidationResponse(responseData);
  }

  private static resolveSchema(response: AxiosResponse) {
    const responseData = SomlService.resolveResponse(response);
    return SomlUtils.parseSchemaJsonLd(responseData);
  }

  private static resolveSchemaResponse(response: AxiosResponse): SchemaResponse {
    const responseData = SomlService.resolveResponse(response);
    const schemaResponse: SchemaResponse = {response: responseData};
    if (SomlService.isSOMLResponse(responseData)) {
      schemaResponse.schema = SomlUtils.parseSchemaJsonLd(responseData);
    }
    return schemaResponse;
  }

  private static isSOMLResponse(responseData: any) {
    return responseData["@type"] === 'SOML' && !!responseData.input;
  }

}

export default SomlService;
